/**
 * theme — which of the two cinematic worlds is stamped on <html> (TEM-1239).
 *
 * The colours themselves live in CSS and only in CSS: Radio's own dark block in
 * src/cinematic.css, and the ecosystem's light layer at
 * `@tempest/design-tokens/css/cinematic-light.css`. Both are imported in App.tsx.
 * This module owns exactly one decision — WHICH id goes on the root element — and
 * deliberately owns no colour values. A JS constant duplicating a CSS token pins
 * one theme's value and drifts the moment the token moves; `utils/driftColor.ts`
 * already reads `--ground` back off the live document for precisely that reason.
 *
 * ── What this is NOT ─────────────────────────────────────────────────────────
 * It is not the revival of what TEM-1195 deleted. That was a `'light' | 'dark'`
 * axis with its OWN palette in themes.css, a sun/moon toggle that changed one of
 * 168 root properties, and a WaveformViewer branch that painted a 17.49:1
 * near-white canvas into the near-black player. None of it returns: there is no
 * ThemeToggle, no ThemeContext, no `[data-theme="light"]` rule, and no component
 * branches on the value. The ids below are the ECOSYSTEM's, shared by name with
 * Record desktop and Record web.
 *
 * There is no UI control yet — flipping the theme means writing the storage key,
 * which is deliberate for this pass: TEM-1239's scope is making the shared layer
 * REACH Radio and render, not designing where the switch lives.
 */

import { refreshDrift } from './driftColor';

/** The two offerable themes. Same two ids, same strings, as Record web's
 *  src/theme.ts — the five retired pre-v3 skins never shipped on Radio. */
export type ThemeId = 'cinematic' | 'cinematic-light';

/**
 * Dark is the boot state and the hero (redesign-decisions.md, "Color system
 * (locked)"). Deliberately NOT seeded from `prefers-color-scheme`: reversing the
 * locked default is Rich's call, and seeding it would also make Radio disagree
 * with Record on the same account and the same OS setting.
 */
export const DEFAULT_THEME: ThemeId = 'cinematic';

/** Shared with Record by name, not by storage: thetempestradio.com and
 *  thetempestrecord.com are different origins, so this is naming consistency. */
export const STORAGE_KEY = 'tempest-theme';

export function isThemeId(v: unknown): v is ThemeId {
  return v === 'cinematic' || v === 'cinematic-light';
}

/** The stored choice, or the default. Never throws — Safari private mode and a
 *  blocked-cookies profile both make localStorage access raise. */
export function readStoredTheme(): ThemeId {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return isThemeId(saved) ? saved : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

export function persistTheme(id: ThemeId): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch {
    /* storage unavailable — the theme still applies for this session */
  }
}

/**
 * Stamp the theme on the root element. Idempotent.
 *
 * DARK STAMPS NOTHING. Radio's dark block is keyed on bare `:root`, so removing
 * the attribute is what selects it; writing `data-theme="cinematic"` would select
 * the ECOSYSTEM's dark block instead, at (0,2,0), and that block is not Radio's —
 * its --dim is #b9c2d4 against Radio's #8f97a8, its --border alpha is 0.15
 * against 0.08, its grade ramp is a different five colours. Stamping it would
 * silently re-skin the shipped dark world. Light is safe to stamp because Radio
 * declares no light block of its own to be overridden.
 *
 * `color-scheme` is NOT set from here: each theme declares its own (cinematic.css
 * for dark, cinematic-light.css:30 for light), and a second writer would be a
 * second source of truth for one property.
 *
 * The one thing CSS cannot do on its own is `--drift` / `--glow`: applyDrift()
 * writes them as INLINE styles on <html> and an inline style outranks every
 * [data-theme] block, so a theme change cannot fix a stale accent by cascade.
 * refreshDrift() re-derives both against the new ground. It is a no-op until
 * something has played, so this is free at boot.
 */
export function applyTheme(id: ThemeId = DEFAULT_THEME, root?: HTMLElement): void {
  if (typeof document === 'undefined') return;
  const el = root ?? document.documentElement;
  if (id === DEFAULT_THEME) {
    el.removeAttribute('data-theme');
  } else {
    el.setAttribute('data-theme', id);
  }
  if (el === document.documentElement) refreshDrift();
}

/** Boot the theme before first paint: apply whatever was stored. */
export function activateStoredTheme(root?: HTMLElement): ThemeId {
  const id = readStoredTheme();
  applyTheme(id, root);
  return id;
}
